/**
 * Which kind of organisation is signed in.
 *
 * Echo supports two: organisations connected to Planning Center, and
 * standalone organisations that sign in with Microsoft or an email link and
 * build their audience by CSV import or the API.
 *
 * This is the single switch every PCO-specific surface reads. Hiding a button
 * is not access control, though — the server rejects PCO calls from standalone
 * organisations independently.
 */

import { Injectable, inject, signal, computed, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

export type OrgType = 'pco' | 'standalone';

/**
 * Organisations created before standalone support have no `orgType` field.
 * They are all Planning Center organisations, so absence means 'pco'.
 */
const DEFAULT_ORG_TYPE: OrgType = 'pco';

@Injectable({ providedIn: 'root' })
export class OrgContextService {

    private auth = inject(Auth);
    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);

    private readonly _orgId = signal<string | null>(null);
    private readonly _orgType = signal<OrgType>(DEFAULT_ORG_TYPE);
    private readonly _loaded = signal(false);

    readonly orgId = this._orgId.asReadonly();
    readonly orgType = this._orgType.asReadonly();
    readonly loaded = this._loaded.asReadonly();

    /** True when the organisation is connected to Planning Center. */
    readonly hasPco = computed(() => this._orgType() === 'pco');

    /** In-flight load, so concurrent callers share one round trip. */
    private loading: Promise<OrgType> | null = null;

    /**
     * Resolve the organisation type, preferring the custom claim (no read) and
     * falling back to the organisation document.
     *
     * Cached for the lifetime of the session: orgType is set at creation and
     * never changes.
     */
    async load(force = false): Promise<OrgType> {
        if (this._loaded() && !force) return this._orgType();
        if (this.loading && !force) return this.loading;

        this.loading = this.resolve();
        try {
            return await this.loading;
        } finally {
            this.loading = null;
        }
    }

    private async resolve(): Promise<OrgType> {
        const user = this.auth.currentUser;
        if (!user) {
            this._loaded.set(true);
            return this._orgType();
        }

        try {
            const token = await user.getIdTokenResult();
            const orgId = (token.claims['orgId'] as string) || null;
            this._orgId.set(orgId);

            const claimType = token.claims['orgType'] as OrgType | undefined;
            if (claimType === 'pco' || claimType === 'standalone') {
                this._orgType.set(claimType);
                this._loaded.set(true);
                return claimType;
            }

            // No claim: either a token minted before orgType existed, or a
            // session that has not refreshed since. Read the org document.
            if (orgId) {
                const snap = await runInInjectionContext(
                    this.injector,
                    () => getDoc(doc(this.firestore, 'organizations', orgId))
                );
                const stored = snap.exists() ? (snap.data()['orgType'] as OrgType | undefined) : undefined;
                this._orgType.set(stored === 'standalone' ? 'standalone' : DEFAULT_ORG_TYPE);
            }
        } catch (error) {
            // Fail towards Planning Center: a PCO org that briefly loses its
            // PCO menu items is a worse, more confusing failure than a
            // standalone org seeing an item it cannot use.
            console.error('Could not resolve organisation type:', error);
            this._orgType.set(DEFAULT_ORG_TYPE);
        }

        this._loaded.set(true);
        return this._orgType();
    }

    /** Clear cached state on sign-out so the next user starts fresh. */
    reset(): void {
        this._orgId.set(null);
        this._orgType.set(DEFAULT_ORG_TYPE);
        this._loaded.set(false);
        this.loading = null;
    }
}
